import { Link } from '@tanstack/react-router';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import type { DataTableColumnDef } from '@nucleus-commerce/admin-kit/index-table';
import type { OrganizationListItem } from '@nucleus-commerce/api-client/platform';
import { useFormat } from '@nucleus-commerce/i18n/react';
import { Badge } from '@nucleus-commerce/ui/components/badge';

import { Mono } from '@/shared/ui/mono';

import { OrganizationStatusBadge } from './organization-status-badge';

/** Column model of the organizations table; `meta` ties columns to the list's sorts and filters. */
export function useOrganizationColumns() {
  const { t } = useTranslation('organizations');
  const format = useFormat();

  return useMemo<Array<DataTableColumnDef<OrganizationListItem>>>(
    () => [
      {
        id: 'name',
        accessorKey: 'name',
        header: t('columns.name'),
        cell: ({ row }) => (
          <div className="flex min-w-0 flex-col gap-0.5 sm:flex-row sm:items-baseline sm:gap-2">
            <Link
              to="/organizations/$organizationId"
              params={{ organizationId: row.original.id }}
              className="truncate font-medium hover:underline"
              onClick={(e) => e.stopPropagation()}
            >
              {row.original.name}
            </Link>
            <Mono className="truncate">{row.original.slug}</Mono>
          </div>
        ),
        meta: { sortField: 'name', lockVisibility: true },
      },
      {
        id: 'ownerStatus',
        accessorKey: 'ownerStatus',
        header: t('columns.ownerStatus'),
        cell: ({ row }) =>
          row.original.ownerStatus === 'invited' ? (
            <Badge variant="outline" className="font-normal">
              {t('ownerStatus.invited')}
            </Badge>
          ) : (
            <span className="text-muted-foreground">{t('ownerStatus.active')}</span>
          ),
        size: 130,
      },
      {
        id: 'liveStores',
        accessorKey: 'liveStores',
        header: t('columns.liveStores'),
        cell: ({ row }) => (
          <span
            className={
              row.original.liveStores === 0 ? 'text-muted-foreground tabular-nums' : 'tabular-nums'
            }
          >
            {format.number(row.original.liveStores)}
          </span>
        ),
        size: 110,
      },
      {
        id: 'billingEmail',
        accessorKey: 'billingEmail',
        header: t('columns.billingEmail'),
        cell: ({ row }) => (
          <span className="text-muted-foreground">{row.original.billingEmail ?? '—'}</span>
        ),
      },
      {
        id: 'status',
        accessorKey: 'status',
        header: t('columns.status'),
        cell: ({ row }) => <OrganizationStatusBadge status={row.original.status} />,
        meta: { filterKey: 'status' },
        size: 120,
      },
      {
        id: 'createdAt',
        accessorKey: 'createdAt',
        header: t('columns.createdAt'),
        cell: ({ row }) => (
          <span className="text-muted-foreground" title={format.utc(row.original.createdAt)}>
            {format.relative(row.original.createdAt)}
          </span>
        ),
        meta: { sortField: 'createdAt', filterKey: 'createdAt' },
        size: 140,
      },
    ],
    [format, t],
  );
}
